<template>
  <div class="golf-results-container">
    <div class="golf-leaderboard">
      <!-- Leaderboard Header -->
      <div class="leaderboard-header">
        <h3 class="leaderboard-title">Golf Results</h3>
        <span class="player-count">{{ participants.length }} {{ participants.length === 1 ? 'player' : 'players' }}</span>
      </div>

      <!-- Golf Table -->
      <div class="golf-table">
        <!-- Table Header -->
        <div class="table-header" :class="getTableHeaderClass()">
          <div class="col-pos">POS</div>
          <div class="col-player">PLAYER</div>
          <div class="col-rounds" v-if="hasRounds && roundHeaders.length > 0">
            <div class="round-header" v-for="(_, index) in roundHeaders" :key="index">
              <div class="round-number">R{{ index + 1 }}</div>
              <div v-if="getRoundStatus(index)" class="round-header-status" :class="`status-${getRoundStatus(index).replace(/_/g, '-')}`">
                {{ formatRoundStatus(getRoundStatus(index)) }}
              </div>
            </div>
          </div>
          <div class="col-strokes" v-if="group.scoringType === 'points'">STROKES</div>
          <div class="col-total">{{ group.scoringType === 'points' ? 'PTS' : 'TOTAL' }}</div>
          <div class="col-par" v-if="group.scoringType !== 'points'">TO PAR</div>
        </div>

        <!-- Table Body -->
        <div class="table-body">
          <div
            class="table-row"
            v-for="(participant, index) in sortedParticipants"
            :key="participant.id"
            :class="[getRowClass(index + 1), getTableHeaderClass()]"
          >
            <!-- Position -->
            <div class="col-pos">
              <span class="position" :class="getPositionClass(index + 1)">
                {{ participant.position || index + 1 }}
              </span>
            </div>

            <!-- Player -->
            <div class="col-player">
              <div class="player-info">
                <span class="player-name">{{ getParticipantName(participant) }}</span>
                <span v-if="hasRounds && getCompletedRoundsCount(participant) > 0" class="rounds-played">
                  {{ getCompletedRoundsCount(participant) }}/{{ roundHeaders.length }} rounds
                </span>
              </div>
            </div>

            <!-- Round Scores -->
            <div class="col-rounds" v-if="hasRounds && roundHeaders.length > 0">
              <div
                class="round-score"
                v-for="(round, roundIndex) in getRoundScores(participant)"
                :key="roundIndex"
                :class="getRoundScoreClass(round, roundIndex)"
              >
                <template v-if="group.scoringType === 'points'">
                  <div class="stroke-score">{{ formatRoundStrokes(round) }}</div>
                  <div class="point-score">{{ formatRoundPoints(round, roundIndex) }}pts</div>
                </template>
                <template v-else>
                  <div class="stroke-score">{{ formatRoundStrokes(round) }}</div>
                  <div class="par-score">{{ formatRoundPar(round, roundIndex) }}</div>
                </template>
              </div>
            </div>

            <!-- Total Strokes (for Stableford) -->
            <div class="col-strokes" v-if="group.scoringType === 'points'">
              <span class="total-strokes">
                {{ getTotalStrokes(participant) }}
              </span>
            </div>

            <!-- Total Score -->
            <div class="col-total">
              <span class="total-score" :class="getTotalScoreClass(participant)">
                {{ getTotalScore(participant) }}
              </span>
            </div>

            <!-- To Par -->
            <div class="col-par" v-if="group.scoringType !== 'points'">
              <span class="par-score" :class="getToParClass(participant)">
                {{ formatToPar(participant) }}
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- Tournament Info -->
      <div v-if="group.parInfo || group.holes" class="tournament-info">
        <div class="info-grid">
          <div v-if="group.holes" class="info-item">
            <span class="info-label">Holes</span>
            <span class="info-value">{{ group.holes }}</span>
          </div>
          <div v-if="group.parInfo" class="info-item">
            <span class="info-label">Course Par</span>
            <span class="info-value">{{ group.parInfo }}</span>
          </div>
          <div class="info-item">
            <span class="info-label">Completed</span>
            <span class="info-value">{{ getCompletedPlayersCount() }}/{{ participants.length }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent } from 'vue';

export default defineComponent({
  name: 'GolfResults',
  props: {
    participants: {
      type: Array,
      required: true,
      default: () => []
    },
    group: {
      type: Object,
      required: true
    }
  },
  computed: {
    sortedParticipants() {
      return [...this.participants].sort((a, b) => {
        // First sort by position if available
        if (a.position && b.position) {
          return a.position - b.position;
        }

        // If no positions, sort by score
        const scoreA = this.getTotalScore(a);
        const scoreB = this.getTotalScore(b);

        // Handle missing scores
        if (scoreA === '-' && scoreB === '-') return 0;
        if (scoreA === '-') return 1; // A goes after B
        if (scoreB === '-') return -1; // B goes after A

        // Sort logic depends on scoring type
        if (this.group.scoringType === 'points') {
          // Stableford: Higher points = better position (descending)
          return scoreB - scoreA;
        } else {
          // Stroke play: Lower strokes = better position (ascending)
          return scoreA - scoreB;
        }
      });
    },

    hasRounds() {
      return this.participants.some(p => p.rounds && Object.keys(p.rounds).length > 0);
    },

    roundHeaders() {
      if (!this.hasRounds) return [];
      const firstParticipant = this.participants.find(p => p.rounds);
      return firstParticipant ? Object.keys(firstParticipant.rounds) : [];
    }
  },
  methods: {
    getTableHeaderClass() {
      if (this.group.scoringType === 'points') {
        return this.hasRounds && this.roundHeaders.length > 0 ? 'has-rounds-points' : 'no-rounds-points';
      } else {
        return this.hasRounds && this.roundHeaders.length > 0 ? 'has-rounds' : 'no-rounds';
      }
    },

    getParticipantName(participant) {
      if (participant.member && participant.member.profile) {
        return `${participant.member.profile.firstName} ${participant.member.profile.lastName}`;
      }
      if (participant.member) {
        return `${participant.member.firstName || ''} ${participant.member.lastName || ''}`.trim() ||
               participant.member.name || 'Unknown Member';
      }
      return participant.name || 'Unknown';
    },

    getRoundScores(participant) {
      if (!participant.rounds) return [];
      return Object.values(participant.rounds);
    },

    getTotalScore(participant) {
      if (this.group.scoringType === 'points') {
        return this.getTotalPoints(participant);
      } else {
        return this.getTotalStrokes(participant);
      }
    },

    getTotalStrokes(participant) {
      if (!participant.rounds) return participant.strokes || '-';

      let total = 0;
      let hasValidRounds = false;

      Object.values(participant.rounds).forEach((round) => {
        const strokes = parseInt(round.strokes);
        if (strokes && strokes > 0) {
          total += strokes;
          hasValidRounds = true;
        }
      });

      return hasValidRounds ? total : '-';
    },

    getTotalPoints(participant) {
      if (participant.rounds) {
        let totalPoints = 0;
        let hasValidRounds = false;

        Object.values(participant.rounds).forEach((round, index) => {
          // First try to use stored points
          if (round.points !== undefined && round.points !== null && round.points !== '') {
            const points = parseInt(round.points) || 0;
            totalPoints += points;
            hasValidRounds = true;
          } else {
            // Calculate Stableford points from strokes if no stored points
            const strokes = parseInt(round.strokes);
            if (strokes && strokes > 0) {
              const par = this.getParForRound(index);
              const calculatedPoints = this.calculateStablefordPoints(strokes, par);
              totalPoints += calculatedPoints;
              hasValidRounds = true;
            }
          }
        });

        if (hasValidRounds) {
          return totalPoints;
        }
      }

      // Fallback to participant-level points
      if (participant.points !== undefined && participant.points !== null) {
        return participant.points;
      }

      return '-';
    },

    getCompletedRoundsCount(participant) {
      if (!participant.rounds) return 0;

      let completedRounds = 0;
      Object.values(participant.rounds).forEach((round) => {
        const strokes = parseInt(round.strokes);
        if (strokes && strokes > 0) {
          completedRounds++;
        }
      });

      return completedRounds;
    },

    getCompletedPlayersCount() {
      return this.participants.filter(p => {
        const total = this.getTotalScore(p);
        return total !== '-' && total !== 0;
      }).length;
    },

    formatRoundScore(round) {
      if (this.group.scoringType === 'points') {
        return round.points !== undefined && round.points !== null && round.points !== ''
          ? round.points
          : '-';
      } else {
        const strokes = parseInt(round.strokes);
        return strokes && strokes > 0 ? strokes : '-';
      }
    },

    formatRoundStrokes(round) {
      const strokes = parseInt(round.strokes);
      return strokes && strokes > 0 ? strokes : '-';
    },

    formatRoundPoints(round, roundIndex) {
      // First try to use stored points
      if (round.points !== undefined && round.points !== null && round.points !== '') {
        return round.points;
      }

      // Calculate points from strokes if no stored points
      const strokes = parseInt(round.strokes);
      if (strokes && strokes > 0) {
        const par = this.getParForRound(roundIndex);
        return this.calculateStablefordPoints(strokes, par);
      }

      return '-';
    },

    formatRoundPar(round, roundIndex) {
      const strokes = parseInt(round.strokes);
      if (!strokes || strokes <= 0) return '-';

      const par = this.getParForRound(roundIndex);
      const toPar = strokes - par;

      if (toPar === 0) return 'E';
      if (toPar > 0) return `+${toPar}`;
      return `${toPar}`;
    },

    getRoundStatus(roundIndex) {
      if (!this.group.roundStatuses || !this.group.roundStatuses[roundIndex]) {
        return null;
      }
      return this.group.roundStatuses[roundIndex];
    },

    formatRoundStatus(status) {
      const statusMap = {
        'in_progress': 'In Progress',
        'not_started': 'Not Started',
        'scheduled': 'Scheduled',
        'postponed': 'Postponed',
        'cancelled': 'Cancelled'
      };
      return statusMap[status] || status;
    },

    formatToPar(participant) {
      const totalStrokes = this.getTotalStrokes(participant);
      if (totalStrokes === '-') return '-';

      // Calculate total par based on completed rounds for multi-round tournaments
      const totalPar = this.getTotalParForParticipant(participant);
      const toPar = totalStrokes - totalPar;
      if (toPar === 0) return 'E';
      if (toPar > 0) return `+${toPar}`;
      return `${toPar}`;
    },

    getPositionClass(position) {
      if (position === 1) return 'first';
      if (position === 2) return 'second';
      if (position === 3) return 'third';
      if (position <= 10) return 'top-ten';
      return 'default';
    },

    getRowClass(position) {
      if (position === 1) return 'leader-row';
      if (position <= 3) return 'podium-row';
      if (position <= 10) return 'top-ten-row';
      return '';
    },

    getRoundScoreClass(round, roundIndex) {
      // Check if round has a status from group.roundStatuses and no valid strokes
      const roundStatus = this.getRoundStatus(roundIndex);
      if (roundStatus && roundStatus !== 'completed' && (!round.strokes || round.strokes <= 0)) {
        return `status-${roundStatus.replace(/_/g, '-')}`;
      }

      if (this.group.scoringType === 'points') return 'points-score';

      const strokes = parseInt(round.strokes);
      if (!strokes || strokes <= 0) return 'no-score';

      const par = this.getParForRound(roundIndex);
      if (!par) return '';

      const diff = strokes - par;
      if (diff < 0) return 'under-par';
      if (diff > 0) return 'over-par';
      return 'at-par';
    },

    getTotalScoreClass(participant) {
      const total = this.getTotalScore(participant);
      if (total === '-') return 'no-score';
      return this.group.scoringType === 'points' ? 'points-total' : 'strokes-total';
    },

    getToParClass(participant) {
      const totalStrokes = this.getTotalStrokes(participant);
      if (totalStrokes === '-') return 'no-score';

      // Calculate total par based on completed rounds for multi-round tournaments
      const totalPar = this.getTotalParForParticipant(participant);
      const toPar = totalStrokes - totalPar;
      if (toPar < 0) return 'under-par';
      if (toPar > 0) return 'over-par';
      return 'at-par';
    },

    getParForRound(roundIndex) {
      if (this.group.parPerRound && this.group.parPerRound[roundIndex]) {
        return this.group.parPerRound[roundIndex];
      }

      if (this.group.sportConfig?.parTotal) {
        const holes = this.group.holes || 18;
        const parTotal = this.group.sportConfig.parTotal[holes];
        if (parTotal) {
          return parTotal;
        }
      }

      const holes = this.group.holes || this.group.sportConfig?.holes?.[0] || 18;
      if (holes === 9) return 35;
      if (holes === 18) return 72;
      return holes * 4;
    },

    getTotalParForParticipant(participant) {
      // If group has a fixed parInfo, use it (for single round or fixed total par)
      if (this.group.parInfo) {
        return this.group.parInfo;
      }

      // For multi-round tournaments, calculate total par based on completed rounds
      if (!participant.rounds) {
        // If no rounds data, use single round par
        return this.getParForRound(0);
      }

      let totalPar = 0;
      let completedRounds = 0;

      Object.values(participant.rounds).forEach((round, index) => {
        const strokes = parseInt(round.strokes);
        if (strokes && strokes > 0) { // Only count completed rounds
          const roundPar = this.getParForRound(index);
          totalPar += roundPar;
          completedRounds++;
        }
      });

      // If no completed rounds, return single round par
      return completedRounds > 0 ? totalPar : this.getParForRound(0);
    },

    calculateStablefordPoints(strokes, par) {
      const diff = strokes - par;

      // Use configured stableford points if available
      if (this.group.sportConfig?.stablefordPoints) {
        const points = this.group.sportConfig.stablefordPoints;

        // Check for exact match first
        if (points[diff] !== undefined) {
          return parseInt(points[diff]) || 0;
        }

        // Check for "3+" (triple bogey or worse)
        if (diff >= 3 && points['3+'] !== undefined) {
          return parseInt(points['3+']) || 0;
        }
      }

      // Fallback: Standard Stableford scoring system
      if (diff <= -3) return 6; // Albatross or better
      if (diff === -2) return 5; // Eagle
      if (diff === -1) return 4; // Birdie
      if (diff === 0) return 3;  // Par
      if (diff === 1) return 2;  // Bogey
      if (diff === 2) return 1;  // Double Bogey
      return 0; // Triple Bogey or worse
    }
  }
});
</script>

<style lang="scss" scoped>
.golf-results-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0.5rem;
}

.golf-leaderboard {
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  overflow: hidden;
}

.leaderboard-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.5rem;
  border-bottom: 1px solid #f3f4f6;
  background: #fafafa;

  .leaderboard-title {
    font-size: 1.1rem;
    font-weight: 600;
    color: #111827;
    margin: 0;
  }

  .player-count {
    color: #6b7280;
    font-size: 0.75rem;
    font-weight: 400;
  }
}

.golf-table {
  .table-header {
    display: grid;
    gap: 0.75rem;
    padding: 0.75rem 1rem;
    background: #f9fafb;
    border-bottom: 1px solid #e5e7eb;
    font-weight: 500;
    color: #6b7280;
    font-size: 0.7rem;
    text-transform: uppercase;
    letter-spacing: 0.025em;
    align-items: center;

    &.has-rounds {
      grid-template-columns: 50px 2fr 2fr 70px 70px 60px;
    }

    &.no-rounds {
      grid-template-columns: 50px 2fr 80px 60px;
    }

    &.has-rounds-points {
      grid-template-columns: 50px 2fr 2fr 70px 70px;
    }

    &.no-rounds-points {
      grid-template-columns: 50px 2fr 70px 70px;
    }

    .col-rounds {
      display: flex;
      gap: 0.25rem;
      justify-content: space-around;

      .round-header {
        font-weight: 500;
        min-width: 30px;
        text-align: center;
        font-size: 0.65rem;
        display: flex;
        flex-direction: column;
        gap: 0.25rem;
        align-items: center;

        .round-number {
          font-weight: 600;
        }

        .round-header-status {
          font-size: 0.55rem;
          font-weight: 500;
          padding: 0.15rem 0.25rem;
          border-radius: 2px;
          line-height: 1;
          white-space: nowrap;

          &.status-in-progress {
            background: #fef3c7;
            color: #92400e;
          }

          &.status-not-started {
            background: #f3f4f6;
            color: #6b7280;
          }

          &.status-scheduled {
            background: #e0e7ff;
            color: #4338ca;
          }

          &.status-postponed {
            background: #fef2f2;
            color: #991b1b;
          }

          &.status-cancelled {
            background: #fee2e2;
            color: #7f1d1d;
          }

          &.status-completed {
            background: #d1fae5;
            color: #065f46;
          }
        }
      }
    }
  }

  .table-body {
    .table-row {
      display: grid;
      gap: 0.75rem;
      padding: 0.75rem 1rem;
      border-bottom: 1px solid #f3f4f6;
      transition: background 0.1s ease;
      align-items: center;

      &.has-rounds {
        grid-template-columns: 50px 2fr 2fr 70px 70px 60px;
      }

      &.no-rounds {
        grid-template-columns: 50px 2fr 80px 60px;
      }

      &.has-rounds-points {
        grid-template-columns: 50px 2fr 2fr 70px 70px;
      }

      &.no-rounds-points {
        grid-template-columns: 50px 2fr 70px 70px;
      }

      &:hover {
        background: #f9fafb;
      }

      &.leader-row {
        background: #fffbeb;
        border-left: 2px solid #f59e0b;
      }

      &.podium-row {
        background: #f8fafc;
        border-left: 2px solid #9ca3af;
      }

      &.top-ten-row {
        background: #fef2f2;
        border-left: 2px solid #ea580c;
      }

      &:last-child {
        border-bottom: none;
      }

      .col-rounds {
        display: flex;
        gap: 0.25rem;
        justify-content: space-around;
      }
    }
  }
}

.col-pos {
  .position {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 1.75rem;
    height: 1.75rem;
    border-radius: 4px;
    font-weight: 600;
    font-size: 0.8rem;

    &.first {
      background: #f59e0b;
      color: white;
    }

    &.second {
      background: #9ca3af;
      color: white;
    }

    &.third {
      background: #ea580c;
      color: white;
    }

    &.top-ten {
      background: #3b82f6;
      color: white;
    }

    &.default {
      background: #f3f4f6;
      color: #6b7280;
    }
  }
}

.col-player {
  .player-info {
    .player-name {
      display: block;
      font-size: 0.85rem;
      font-weight: 500;
      color: #111827;
      line-height: 1.2;
    }

    .rounds-played {
      display: block;
      font-size: 0.7rem;
      color: #9ca3af;
      margin-top: 0.1rem;
      font-weight: 400;
    }
  }
}

.round-score {
  font-size: 0.75rem;
  font-weight: 500;
  padding: 0.125rem 0.25rem;
  border-radius: 2px;
  text-align: center;
  min-width: 30px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.05rem;
  border: 1px solid;

  &.under-par {
    background: #f0fdf4;
    border-color: #bbf7d0;
    color: #059669;
  }

  &.over-par {
    background: #fef2f2;
    border-color: #fca5a5;
    color: #dc2626;
  }

  &.at-par {
    background: #f0f9ff;
    border-color: #93c5fd;
    color: #0891b2;
  }

  &.points-score {
    background: #fffbeb;
    border-color: #fcd34d;
    color: #f59e0b;
  }

  &.no-score {
    color: #9ca3af;
    background: transparent;
    border-color: #e5e7eb;
  }

  .stroke-score {
    font-size: 0.75rem;
    font-weight: 600;
    line-height: 1;
  }

  .point-score {
    font-size: 0.65rem;
    font-weight: 500;
    opacity: 0.8;
    line-height: 1;
  }

  .par-score {
    font-size: 0.65rem;
    font-weight: 500;
    opacity: 0.8;
    line-height: 1;
  }

  .round-status {
    font-size: 0.6rem;
    font-weight: 500;
    line-height: 1.2;
    text-align: center;
    white-space: nowrap;
  }

  &.status-in-progress {
    background: #fef3c7;
    border-color: #fbbf24;
    color: #92400e;
  }

  &.status-not-started {
    background: #f3f4f6;
    border-color: #d1d5db;
    color: #6b7280;
  }

  &.status-scheduled {
    background: #e0e7ff;
    border-color: #a5b4fc;
    color: #4338ca;
  }

  &.status-postponed {
    background: #fef2f2;
    border-color: #fca5a5;
    color: #991b1b;
  }

  &.status-cancelled {
    background: #fee2e2;
    border-color: #f87171;
    color: #7f1d1d;
  }
}

.col-strokes {
  .total-strokes {
    font-size: 0.85rem;
    font-weight: 500;
    color: #374151;
    text-align: center;
  }
}

.col-total {
  .total-score {
    font-size: 1rem;
    font-weight: 600;
    text-align: center;

    &.strokes-total {
      color: #374151;
    }

    &.points-total {
      color: #f59e0b;
    }

    &.no-score {
      color: #9ca3af;
    }
  }
}

.col-par {
  .par-score {
    font-size: 0.85rem;
    font-weight: 600;
    padding: 0.125rem 0.25rem;
    border-radius: 2px;
    text-align: center;
    border: 1px solid;

    &.under-par {
      background: #f0fdf4;
      border-color: #bbf7d0;
      color: #059669;
    }

    &.over-par {
      background: #fef2f2;
      border-color: #fca5a5;
      color: #dc2626;
    }

    &.at-par {
      background: #f0f9ff;
      border-color: #93c5fd;
      color: #0891b2;
    }

    &.no-score {
      color: #9ca3af;
      background: transparent;
      border-color: transparent;
    }
  }
}

.tournament-info {
  padding: 1rem;
  background: #fafafa;
  border-top: 1px solid #e5e7eb;

  .info-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(100px, 1fr));
    gap: 1rem;
  }

  .info-item {
    text-align: center;

    .info-label {
      display: block;
      font-size: 0.65rem;
      color: #6b7280;
      font-weight: 500;
      text-transform: uppercase;
      letter-spacing: 0.025em;
      margin-bottom: 0.125rem;
    }

    .info-value {
      display: block;
      font-size: 0.85rem;
      font-weight: 600;
      color: #374151;
    }
  }
}

// Mobile Responsiveness
@media (max-width: 768px) {
  .golf-results-container {
    padding: 0.25rem;
  }

  .leaderboard-header {
    padding: 0.75rem 1rem;

    .leaderboard-title {
      font-size: 1rem;
    }

    .player-count {
      font-size: 0.7rem;
    }
  }

  .golf-table {
    .table-header {
      &.has-rounds {
        grid-template-columns: 40px 1fr 50px 50px 45px;
      }
      &.no-rounds {
        grid-template-columns: 40px 1fr 50px 45px;
      }
      &.has-rounds-points {
        grid-template-columns: 40px 1fr 50px 50px 50px;
      }
      &.no-rounds-points {
        grid-template-columns: 40px 1fr 50px 50px;
      }
      padding: 0.5rem 0.75rem;
      gap: 0.5rem;
      font-size: 0.65rem;

      .col-rounds {
        display: none;
      }
    }

    .table-body .table-row {
      &.has-rounds {
        grid-template-columns: 40px 1fr 50px 50px 45px;
      }
      &.no-rounds {
        grid-template-columns: 40px 1fr 50px 45px;
      }
      &.has-rounds-points {
        grid-template-columns: 40px 1fr 50px 50px 50px;
      }
      &.no-rounds-points {
        grid-template-columns: 40px 1fr 50px 50px;
      }
      padding: 0.5rem 0.75rem;
      gap: 0.5rem;

      .col-rounds {
        display: none;
      }
    }
  }

  .col-pos .position {
    width: 1.5rem;
    height: 1.5rem;
    font-size: 0.7rem;
  }

  .col-player .player-info .player-name {
    font-size: 0.8rem;
  }

  .col-strokes .total-strokes {
    font-size: 0.75rem;
  }

  .col-total .total-score {
    font-size: 0.9rem;
  }

  .col-par .par-score {
    font-size: 0.75rem;
  }

  .tournament-info {
    padding: 0.75rem;

    .info-grid {
      grid-template-columns: repeat(3, 1fr);
      gap: 0.75rem;
    }

    .info-item {
      .info-label {
        font-size: 0.6rem;
      }

      .info-value {
        font-size: 0.8rem;
      }
    }
  }
}
</style>